/**
 * reportApi — pure axios functions for report endpoints.
 *
 * These functions are intentionally side-effect free so they can be used
 * directly in React Query hooks.
 */

import api from "./api_instance";
import { API_ENDPOINTS } from "../constants/api";
import { ReportByCodeResponse } from "../types/report";

export type ReportExportFormat = "xlsx" | "csv";

const withReportCode = (endpoint: string, reportCode: string): string =>
  endpoint.replace(":report_code", encodeURIComponent(reportCode));

/** GET /reports */
export const getReportsApi = async <T = unknown>(): Promise<T> => {
  const { data } = await api.get<T>(API_ENDPOINTS.REPORTS.LIST);
  return data;
};

/** GET /reports/:report_code */
export const getReportByCodeApi = async (
  reportCode: string,
): Promise<ReportByCodeResponse> => {
  const { data } = await api.get<ReportByCodeResponse>(
    withReportCode(API_ENDPOINTS.REPORTS.DETAIL, reportCode),
  );
  return data;
};

/** POST /reports/:report_code/run */
export const runReportApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  reportCode: string,
  payload?: TPayload,
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withReportCode(API_ENDPOINTS.REPORTS.RUN, reportCode),
    payload ?? {},
  );
  return data;
};

/** POST /reports/:report_code/export */
export const exportReportApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  reportCode: string,
  payload?: TPayload,
  format: ReportExportFormat = "xlsx",
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withReportCode(API_ENDPOINTS.REPORTS.EXPORT, reportCode),
    payload ?? {},
    { params: { format } },
  );
  return data;
};

/** GET {endpoint} */
export const getSelectData = async <TResponse = unknown>(
  endpoint: string,
): Promise<TResponse> => {
  const { data } = await api.get<TResponse>(endpoint);
  return data;
};
